import React, { useEffect, useState } from "react";
import axios from "axios";
import { Radio, ExternalLink, AlertTriangle, Wrench } from "lucide-react";
import { cn } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONE = {
  operational: { strip: "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200/60 dark:border-emerald-900/50", text: "text-emerald-900 dark:text-emerald-200", dot: "bg-emerald-500" },
  maintenance: { strip: "bg-sky-50 dark:bg-sky-950/40 border-sky-200/60 dark:border-sky-900/50", text: "text-sky-900 dark:text-sky-200", dot: "bg-sky-500" },
  degraded: { strip: "bg-amber-50 dark:bg-amber-950/40 border-amber-200/60 dark:border-amber-900/50", text: "text-amber-900 dark:text-amber-200", dot: "bg-amber-500" },
  outage: { strip: "bg-rose-50 dark:bg-rose-950/40 border-rose-200/60 dark:border-rose-900/50", text: "text-rose-900 dark:text-rose-200", dot: "bg-rose-500" },
};

export default function TrustCenterStatusStrip() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => axios.get(`${API}/status/summary?window=30d`).then((r) => setData(r.data)).catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, []);

  if (!data) return <div className="h-14 rounded-xl bg-muted/40 animate-pulse" data-testid="trust-center-status-strip-loading" />;

  const status = data.status || "operational";
  const label = data.label || "All Systems Operational";
  const tone = TONE[status] || TONE.operational;
  const uptimeVals = (data.checks || []).map((c) => c.uptime_pct).filter((v) => typeof v === "number");
  const avgUptime = uptimeVals.length ? (uptimeVals.reduce((a, b) => a + b, 0) / uptimeVals.length).toFixed(2) : null;
  const incidents = data.active_incidents || [];
  const maintenances = data.active_maintenances || [];

  return (
    <div className={cn("rounded-xl border px-4 py-3", tone.strip)} data-testid="trust-center-status-strip">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0 flex-wrap">
          <span className={cn("inline-block w-2.5 h-2.5 rounded-full pulse-dot shrink-0", tone.dot)} />
          <span className={cn("text-[13.5px] font-semibold", tone.text)} data-testid="trust-center-status-label">{label}</span>
          {data.live && (
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold bg-white/60 dark:bg-black/20">
              <Radio className="w-3 h-3" /> LIVE
            </span>
          )}
          {avgUptime && (
            <span className="text-[11.5px] text-muted-foreground" data-testid="trust-center-status-uptime">{avgUptime}% uptime (30d)</span>
          )}
        </div>
        {data.page_url && (
          <a
            href={data.page_url}
            target="_blank"
            rel="noreferrer noopener"
            className="text-[12px] font-medium text-primary hover:underline inline-flex items-center gap-1 shrink-0"
            data-testid="trust-center-status-page-link"
          >
            Full status history <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {(incidents.length > 0 || maintenances.length > 0) && (
        <div className="mt-2.5 pt-2.5 border-t border-black/5 dark:border-white/10 space-y-1.5">
          {incidents.map((i) => (
            <div key={i.id} className="flex items-start gap-2" data-testid={`trust-center-status-incident-${i.id}`}>
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
              <span className="text-[12.5px] font-medium">{i.title}</span>
            </div>
          ))}
          {maintenances.map((m) => (
            <div key={m.id} className="flex items-start gap-2" data-testid={`trust-center-status-maintenance-${m.id}`}>
              <Wrench className="w-3.5 h-3.5 text-sky-600 shrink-0 mt-0.5" />
              <span className="text-[12.5px] font-medium">{m.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
